import { ExternalLinkIcon } from "@heroicons/react/outline";
import { Link } from "react-router-dom";

import type { SpanEntity, SubtreeToolCall } from "./utils";

import { getEntityColor } from "@/components/UnifiedRegistry/utils";
import EntityIcon from "@/components/ui/icons/EntityIcon";
import { useUser } from "@/context/user-context";
import { EntityType } from "@/queries/folder-entities/types";
import { routePaths } from "@/utils/routePaths";

const CardShell = ({
  icon,
  title,
  subtitle,
  to,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  to?: string;
  onClick?: () => void;
}) => {
  const body = (
    <div className="flex w-full items-center gap-3 rounded-lg border border-gray-200 px-3 py-2.5 text-left transition-colors hover:bg-gray-50">
      {icon}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-gray-900">{title}</span>
        {subtitle ? <span className="block truncate text-xs text-gray-500">{subtitle}</span> : null}
      </span>
      {to ? <ExternalLinkIcon className="h-4 w-4 shrink-0 text-gray-400" /> : null}
    </div>
  );
  if (to) {
    return <Link to={to}>{body}</Link>;
  }
  if (onClick) {
    return (
      <button type="button" className="w-full" onClick={onClick}>
        {body}
      </button>
    );
  }
  return body;
};

const EntityIconBadge = ({ type }: { type: EntityType }) => (
  <span
    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${getEntityColor(type)}`}
  >
    <EntityIcon type={type} size="xs" className="h-4 w-4" />
  </span>
);

export const PromptEntityCard = ({ entity }: { entity: SpanEntity }) => {
  const workspaceId = useUser()?.activeWorkspaceId;
  const to =
    workspaceId && entity.registryId
      ? routePaths.workspacePrompt(workspaceId, entity.registryId, entity.version)
      : undefined;
  return (
    <CardShell
      icon={<EntityIconBadge type={EntityType.PROMPT} />}
      title={entity.name}
      subtitle={entity.version ? `Version ${entity.version}` : undefined}
      to={to}
    />
  );
};

export const ToolCallCard = ({
  call,
  onJumpToSpan,
}: {
  call: SubtreeToolCall;
  onJumpToSpan?: (spanId: string) => void;
}) => (
  <CardShell
    icon={<EntityIconBadge type={EntityType.TOOL} />}
    title={call.name}
    subtitle={call.isSelectedSpan ? "Selected span" : `Tool: ${call.name}`}
    onClick={call.isSelectedSpan ? undefined : () => onJumpToSpan?.(call.spanId)}
  />
);
